/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import useModals from "../../hooks/useModals";
import { TableProps } from "../../interfaces/table";
import { SwitchTableButton } from "../ui/buttons/SwitchTableButton";
import Assignation from "./Assignation";
import { useSocialStore } from "../../stores/social";
import DeleteModal from "../ui/modals/DeleteModal";
import { useUserContext } from "../../contexts/userContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Badge,
  CheckIcon,
  DesktopTable,
  IconButton,
  MicroLabel,
  MobileCardFooter,
  MobileCardList,
  MobileField,
  MobileFieldGrid,
  TableEmptyState,
  TableFrame,
  Td,
  Th,
  TrashIcon,
  cardClasses,
  renderCellValue,
  rowClasses,
} from "../ui/tables/tableUi";

const TableSocial = ({ columns, data }: TableProps) => {
  const { t } = useTranslation();
  const {
    tabSocial,
    setTabSocial,
    setIdCustomer,
    setIdEmployee,
    setTabElements,
    setIsCompleted
  } = useSocialStore();
  const { typeRole, sc_UserToCustomerFile } = useUserContext();
  const navigate = useNavigate();
  // Modals
  const { modals, openModals, openModal, closeModal } = useModals();

  const [primaryColumn, ...secondaryColumns] = columns;

  // Le client a-t-il accès aux bulletins de ce dossier ?
  const hasBulletinAccess = (row: any) =>
    typeRole === "Company" ||
    (typeRole === "Customer" &&
      sc_UserToCustomerFile.find(
        (customer: any) =>
          customer.idCustomerFile === row.id && customer.bulletin === true
      ));

  // Rendu des statuts et des valeurs de cellule
  const renderCell = (col: { accessor: string }, row: any) => {
    if (col.accessor === "Validation") {
      return row.sc_PaySlipsValidated === true ? (
        <Badge tone="emerald" icon={<CheckIcon />}>
          {t("Validé")}
        </Badge>
      ) : null;
    }

    if (col.accessor === "isFinished") {
      if (row.isFinished) {
        return (
          <Badge tone="emerald" icon={<CheckIcon />}>
            {t("Validé")}
          </Badge>
        );
      }
      return (row.sc_Monthly?.length ?? 0) > 0 ||
        (row.sc_Absences?.length ?? 0) > 0 ? (
        <Badge tone="amber">{t("En cours")}</Badge>
      ) : null;
    }

    if (col.accessor === "completed") {
      return row.completed ? (
        <Badge tone="emerald" icon={<CheckIcon />}>
          {t("Validé")}
        </Badge>
      ) : null;
    }

    return renderCellValue(row[col.accessor] ?? null);
  };

  // Boutons de navigation d'une ligne, partagés table / cartes
  const renderNavigationActions = (row: any) => (
    <div className="flex flex-wrap items-center gap-2">
      {tabSocial === "Dossier clients social" && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (typeRole === "Company") {
              navigate(`/social/payroll-simulator/${row.id}`);
            } else {
              navigate(`/customer-area/social/payroll-simulator/${row.id}`);
            }
          }}
          className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 font-Nunito text-xs font-semibold whitespace-nowrap text-slate-600 transition-colors hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 focus-visible:ring-2 focus-visible:ring-indigo-200 focus-visible:outline-none"
        >
          {t("Simulateur de paie")}
        </button>
      )}

      {tabSocial === "Dossier clients social" && row.monthName !== undefined ? (
        <>
          {/* Bouton pour passer aux bulletins si le client a les bulletins activés */}
          {hasBulletinAccess(row) ? (
            <SwitchTableButton
              text={"Bulletins"}
              id={row.id}
              idSetter={setIdCustomer}
              setter={setTabSocial}
            />
          ) : null}
          <SwitchTableButton
            text={"Salariés"}
            id={row.id}
            idSetter={setIdCustomer}
            setter={setTabSocial}
            setIsCompleted={setIsCompleted}
            isCompleted={row?.sc_Completed}
          />
        </>
      ) : tabSocial === "Salariés" ? (
        <SwitchTableButton
          text={"Eléments"}
          setTabElements={setTabElements}
          id={row.id}
          idSetter={setIdEmployee}
          setter={setTabSocial}
        />
      ) : null}
    </div>
  );

  // Actions d'assignation et de suppression
  const renderRowActions = (row: any) => (
    <div className="flex items-center gap-1.5">
      {/* Assignation pour Company sur Dossier clients social */}
      {tabSocial === "Dossier clients social" && typeRole === "Company" && (
        <IconButton
          label={t("Assigner")}
          tone="blue"
          onClick={(e) => {
            e.stopPropagation();
            openModal(
              "assignation",
              <Assignation closeModal={() => closeModal("assignation")} idCustomerFile={row.id} />
            );
          }}
        >
          <svg
            aria-hidden="true"
            className="size-4.5"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
            />
          </svg>
        </IconButton>
      )}

      {/* Suppression pour Company ou Customer sur Salariés non validé */}
      {tabSocial === "Salariés" &&
        (typeRole === "Company" ||
          (typeRole === "Customer" && row.sc_Completed === false)) && (
          <IconButton
            label={t("Supprimer")}
            tone="danger"
            onClick={(e) => {
              e.stopPropagation();
              openModal(
                "deleteModal",
                <DeleteModal closeModal={() => closeModal("deleteModal")} id={row.id} />
              );
            }}
          >
            <TrashIcon />
          </IconButton>
        )}
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Affichage des modales en fonction de openModals */}
      {Object.keys(modals).map((modalId) =>
        openModals[modalId] ? <div key={modalId}>{modals[modalId]}</div> : null
      )}

      <TableFrame>
        {data.length === 0 ? (
          <TableEmptyState
            title={t("Aucune donnée disponible")}
            description={t("Les données apparaîtront ici une fois ajoutées")}
          />
        ) : (
          <>
            {/* Vue tableau — écrans larges */}
            <DesktopTable>
              <thead className="sticky top-0 z-10">
                <tr>
                  {columns.map((col, index) => (
                    <Th key={index} width={index === 0 ? "24%" : undefined}>
                      {col.header}
                    </Th>
                  ))}
                  <Th>{t("Actions")}</Th>
                  <Th align="right" className="w-px" />
                </tr>
              </thead>
              <tbody className="font-Nunito">
                {data.map((row) => (
                  <React.Fragment key={row.id}>
                    <tr className={rowClasses({ clickable: false })}>
                      {columns.map((col, colIndex) => (
                        <Td
                          key={colIndex}
                          className={
                            colIndex === 0
                              ? "font-semibold text-slate-900"
                              : "text-slate-600"
                          }
                        >
                          {renderCell(col, row)}
                        </Td>
                      ))}

                      {/* Première colonne d'action (navigation) */}
                      <Td>{renderNavigationActions(row)}</Td>

                      {/* Deuxième colonne d'action (assignation/suppression) */}
                      <Td>
                        <div className="flex justify-end">
                          {renderRowActions(row)}
                        </div>
                      </Td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </DesktopTable>

            {/* Vue cartes — mobile et tablette */}
            <MobileCardList>
              {data.map((row) => (
                <li key={row.id}>
                  <div className={cardClasses({})}>
                    <div className="min-w-0">
                      <MicroLabel>{primaryColumn?.header}</MicroLabel>
                      <div className="mt-0.5 text-[15px] leading-snug font-bold wrap-break-word text-slate-900">
                        {primaryColumn ? renderCell(primaryColumn, row) : null}
                      </div>
                    </div>

                    {secondaryColumns.length > 0 && (
                      <MobileFieldGrid>
                        {secondaryColumns.map((col) => (
                          <MobileField
                            key={col.accessor}
                            label={col.header}
                            value={renderCell(col, row)}
                          />
                        ))}
                      </MobileFieldGrid>
                    )}

                    <MobileCardFooter>
                      {renderNavigationActions(row)}
                      {renderRowActions(row)}
                    </MobileCardFooter>
                  </div>
                </li>
              ))}
            </MobileCardList>
          </>
        )}
      </TableFrame>
    </div>
  );
};

export default TableSocial;
